import { makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import mentorsAPI, { MentorNote as MentorNoteType } from '../../api/mentors';
import {
  Note,
  NewNote,
  PageLoader,
  SnackMessage,
} from '../../components/common';
import { MentorContext } from '../../contexts/mentor-context';
import { NoteType } from '../../contexts/personal-notes-context';
import { UserContext } from '../../contexts/user-context';
import { useNoteScroll } from '../../hooks/useNoteScroll';
import { StringDate } from '../../utils/date/StringDate';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
  },
});

function MentorNotesPage() {
  const classes = useStyles();
  const { hasAccessToAction } = useContext(UserContext);
  const { mentorId } = useParams<{ mentorId: string }>();
  const { mentor } = useContext(MentorContext);
  const [updatingNotesIds, setUpdaingNotesIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [deletingNotesIds, setDeletingNotesIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [notes, setNotes] = useState<MentorNoteType[]>([]);
  const [activeNoteForm, setActiveNoteForm] = useState<boolean>(false);
  const noteType = NoteType.MentorNote;

  const { enqueueSnackbar } = useSnackbar();

  // Use the custom hook for note scrolling
  useNoteScroll({ notes, isLoading });

  const handleCreateNote = useCallback(
    async (
      note: {
        contents: string;
        attachmentRefs: string;
        creationDate: StringDate;
      },
      callback,
    ) => {
      try {
        setIsCreating(true);
        if (mentor) {
          const createdNotes = (await mentorsAPI.createNote({
            mentorId: mentor.id,
            contents: note.contents,
            attachmentRefs: note.attachmentRefs,
            creationDate: note.creationDate,
          })) as MentorNoteType;
          setNotes((prev) => [createdNotes, ...prev]);
        }
        setIsCreating(false);
        callback();
        enqueueSnackbar('The note was successfully added.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while creating the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setIsCreating(false);
      }
    },
    [mentor, enqueueSnackbar],
  );

  const handleNoteUpdate = useCallback(
    async (note: MentorNoteType, callback) => {
      try {
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: true }));
        if (mentor) {
          await mentorsAPI.updateNote(note);
          setNotes((prev) =>
            prev.map((mentorNotes) =>
              mentorNotes.id === note.id ? note : mentorNotes,
            ),
          );
        }
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: false }));
        callback();
        enqueueSnackbar('The note was successfully updated.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while updating the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: false }));
      }
    },
    [mentor, enqueueSnackbar],
  );

  const handleNoteRemove = useCallback(
    async (note: MentorNoteType, callback: () => any) => {
      try {
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: true }));
        if (mentor) {
          await mentorsAPI.removeNote(mentor.id, note.id);
          setNotes((prev) =>
            prev.filter((mentorNote) => mentorNote.id !== note.id),
          );
        }
        callback();
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: false }));
        enqueueSnackbar('The note was successfully removed.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while removing the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: false }));
      }
    },
    [mentor, enqueueSnackbar],
  );

  const handleUploadFile = useCallback(
    async (file: File) => {
      try {
        const loadedFileURL = await mentorsAPI.attachFileToNote(mentorId, file);
        return loadedFileURL;
      } catch (e: any) {
        return '';
      }
    },
    [mentorId],
  );

  const loadNotes = async (mentorId: string) => {
    try {
      setIsLoading(true);
      const responseNotes = await mentorsAPI.getNotes(mentorId);

      const sortNotes = responseNotes.sort((a, b) => {
        const prevDate = +new Date(a.creationDate);
        const nextDate = +new Date(b.creationDate);
        return nextDate - prevDate;
      });

      setNotes(sortNotes);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotes(mentorId);
  }, [mentorId]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {hasAccessToAction('mentor.notes.update') && (
        <NewNote
          creating={isCreating}
          disabled={activeNoteForm}
          noteType={noteType}
          onUploadFile={handleUploadFile}
          onCreate={handleCreateNote}
          onCreateModeChange={setActiveNoteForm}
        />
      )}
      <div>
        {notes.map((note, index) => (
          <Note<MentorNoteType>
            key={note.id}
            note={note}
            updating={updatingNotesIds[note.id]}
            deleting={deletingNotesIds[note.id]}
            disabledEdit={activeNoteForm}
            onUploadFile={handleUploadFile}
            onEdit={handleNoteUpdate}
            onRemove={handleNoteRemove}
            readOnly={!hasAccessToAction('mentor.notes.update')}
            onEditModeChange={setActiveNoteForm}
            noteType={noteType}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

export default MentorNotesPage;
